import { ContentCard } from "@msuite/ui/content-card";
import { DataGrid } from "@msuite/ui/data-grid";
import { icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { WorkspaceTag } from "@msuite/ui/tag";
import { HintTooltip } from "@msuite/ui/tooltip";
import { moment } from "@msuite/utils/date";
import { guardString } from "@msuite/utils/strings";
import type { IncomingInvoiceDetail, Nullable } from "@msuite/utils/types";
import {
	formatInvoiceAmount,
	formatInvoiceDate,
	formatInvoiceRate,
} from "@/components/incoming-invoices";
import { IncomingInvoiceEditButton } from "./incoming-invoice-edit-button";
import { IncomingInvoicePartnerReportLink } from "./incoming-invoice-partner-report-link";

/** Props Interface */
interface IncomingInvoiceDetailsProps {
	incomingInvoice: Nullable<IncomingInvoiceDetail>;
}

export function IncomingInvoiceDetails({ incomingInvoice }: IncomingInvoiceDetailsProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Computed */
	const currency = incomingInvoice?.currency ?? "EUR";
	const amount = (value: number | null | undefined) => formatInvoiceAmount(value ?? null, currency);
	const skonto = incomingInvoice?.skontoRate
		? t("incoming_invoices.skonto_summary", {
				rate: formatInvoiceRate(incomingInvoice.skontoRate),
				date: formatInvoiceDate(incomingInvoice.skontoDeadline),
			})
		: guardString(null);

	const receivedAt = incomingInvoice?.receivedAt
		? moment(incomingInvoice.receivedAt).longFormat()
		: guardString(null);

	/** Render */
	return (
		<ContentCard
			title={t("incoming_invoices.invoice")}
			icon={icon("ReceiptText")}
			accessory={<IncomingInvoiceEditButton incomingInvoice={incomingInvoice} />}
		>
			<DataGrid>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.number")}</DataGrid.Label>
					<DataGrid.Value>{guardString(incomingInvoice?.number)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("organization.workspace")}</DataGrid.Label>
					<DataGrid.Value>
						{incomingInvoice?.workspace ? (
							<WorkspaceTag
								size="md"
								workspace={incomingInvoice.workspace}
							/>
						) : (
							guardString(null)
						)}
					</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.invoice_date")}</DataGrid.Label>
					<DataGrid.Value>{formatInvoiceDate(incomingInvoice?.invoiceDate ?? null)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.received_at")}</DataGrid.Label>
					<DataGrid.Cell className="h-stack gap-1">
						<span>{receivedAt}</span>
						<HintTooltip label={t("incoming_invoices.received_at_hint")} />
					</DataGrid.Cell>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.due_date")}</DataGrid.Label>
					<DataGrid.Value>{formatInvoiceDate(incomingInvoice?.dueDate ?? null)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Divider />
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.net_amount")}</DataGrid.Label>
					<DataGrid.Value>{amount(incomingInvoice?.netAmount)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.vat_amount")}</DataGrid.Label>
					<DataGrid.Value>{formatInvoiceRate(incomingInvoice?.vatRate ?? null)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.gross_amount")}</DataGrid.Label>
					<DataGrid.Value>{amount(incomingInvoice?.grossAmount)}</DataGrid.Value>
				</DataGrid.Row>
				<DataGrid.Row>
					<DataGrid.Label>{t("incoming_invoices.skonto_rate")}</DataGrid.Label>
					<DataGrid.Value>{skonto}</DataGrid.Value>
				</DataGrid.Row>
				{incomingInvoice?.reportNumber && (
					<>
						<DataGrid.Divider />
						<DataGrid.Row>
							<DataGrid.Label>{t("incoming_invoices.report_number")}</DataGrid.Label>
							<DataGrid.Value>{incomingInvoice.reportNumber}</DataGrid.Value>
						</DataGrid.Row>
						<DataGrid.Row>
							<DataGrid.Label>{t("incoming_invoices.partner")}</DataGrid.Label>
							<DataGrid.Value>
								{incomingInvoice.partnerReport ? (
									<IncomingInvoicePartnerReportLink partnerReport={incomingInvoice.partnerReport} />
								) : (
									<span className="label">{t("incoming_invoices.report_not_found")}</span>
								)}
							</DataGrid.Value>
						</DataGrid.Row>
					</>
				)}
			</DataGrid>
		</ContentCard>
	);
}
